import React, { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { Search, UserPlus, X, ShieldCheck, GraduationCap, Loader2 } from 'lucide-react';
import { classAPI } from '../../lib/api';

/**
 * Dialog for the class OWNER to add/remove assistants.
 * Assistants can be:
 *   - Any approved professor on the platform
 *   - Any student enrolled in this class
 */
export default function AssistantsManagerDialog({ open, onOpenChange, classId, className, onChange }) {
  const [assistants, setAssistants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState({ professors: [], students: [] });
  const [searching, setSearching] = useState(false);
  const [pendingId, setPendingId] = useState(null);

  const loadAssistants = useCallback(async () => {
    if (!classId) return;
    setLoading(true);
    try {
      const res = await classAPI.listAssistants(classId);
      setAssistants(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudieron cargar los asistentes');
    } finally {
      setLoading(false);
    }
  }, [classId]);

  const runSearch = useCallback(async (q) => {
    if (!classId) return;
    setSearching(true);
    try {
      const res = await classAPI.searchEligibleAssistants(classId, q);
      setResults(res.data || { professors: [], students: [] });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al buscar usuarios');
    } finally {
      setSearching(false);
    }
  }, [classId]);

  useEffect(() => {
    if (open) {
      loadAssistants();
      runSearch('');
    } else {
      setSearch('');
      setResults({ professors: [], students: [] });
    }
  }, [open, loadAssistants, runSearch]);

  // debounce search
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => runSearch(search), 250);
    return () => clearTimeout(t);
  }, [search, open, runSearch]);

  const handleAdd = async (userId, name) => {
    setPendingId(userId);
    try {
      await classAPI.addAssistant(classId, { user_id: userId });
      toast.success(`${name} ahora es asistente`);
      await loadAssistants();
      await runSearch(search);
      if (onChange) onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo agregar');
    } finally {
      setPendingId(null);
    }
  };

  const handleRemove = async (userId, name) => {
    if (!window.confirm(`¿Quitar a ${name} como asistente?`)) return;
    setPendingId(userId);
    try {
      await classAPI.removeAssistant(classId, userId);
      toast.success(`${name} ya no es asistente`);
      await loadAssistants();
      await runSearch(search);
      if (onChange) onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo quitar');
    } finally {
      setPendingId(null);
    }
  };

  const renderUserRow = (u, kind, action) => (
    <div
      key={`${kind}-${u.user_id}`}
      className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 border border-transparent hover:border-slate-100 transition-colors"
      data-testid={`assistant-candidate-${u.user_id}`}
    >
      <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
        {u.photo_url ? (
          <img src={u.photo_url} alt={u.name} className="w-full h-full object-cover" />
        ) : (
          <span className="text-[11px] font-bold text-slate-500">
            {(u.name || '?').slice(0, 2).toUpperCase()}
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-medium text-slate-800 truncate">{u.name}</p>
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
            u.role === 'professor'
              ? 'bg-blue-100 text-blue-700'
              : 'bg-emerald-100 text-emerald-700'
          }`}>
            {u.role === 'professor' ? 'Prof.' : 'Est.'}
          </span>
        </div>
        <p className="text-[11px] text-slate-500 truncate">{u.email}</p>
      </div>
      {action}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col" data-testid="assistants-manager-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
            Asistentes de la Clase
          </DialogTitle>
          <DialogDescription>
            Los asistentes tienen los mismos permisos que tú en <strong>{className}</strong>. Pueden ver estudiantes, aprobar entregas, editar actividades y más.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 flex flex-col gap-4 overflow-hidden py-2">
          {/* Current assistants */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">
              Asistentes actuales ({assistants.length})
            </h3>
            {loading ? (
              <p className="text-xs text-slate-400 py-2 flex items-center gap-1.5">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Cargando...
              </p>
            ) : assistants.length === 0 ? (
              <p className="text-xs text-slate-400 italic py-2">Aún no hay asistentes</p>
            ) : (
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {assistants.map((a) => renderUserRow(a, 'current',
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-red-600 hover:bg-red-50 hover:text-red-700"
                    onClick={() => handleRemove(a.user_id, a.name)}
                    disabled={pendingId === a.user_id}
                    data-testid={`remove-assistant-${a.user_id}`}
                  >
                    {pendingId === a.user_id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <>
                        <X className="w-3.5 h-3.5 mr-1" />
                        Quitar
                      </>
                    )}
                  </Button>
                ))}
              </div>
            )}
          </div>

          {/* Search */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">
              Agregar asistente
            </h3>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <Input
                type="text"
                placeholder="Buscar por nombre o email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="assistants-search-input"
              />
            </div>
          </div>

          {/* Results */}
          <div className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-1">
            {/* Professors */}
            <div>
              <h4 className="text-[11px] font-semibold text-blue-700 mb-1 flex items-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5" />
                Profesores en la plataforma
              </h4>
              {searching ? (
                <p className="text-xs text-slate-400 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" />Buscando...</p>
              ) : results.professors.length === 0 ? (
                <p className="text-xs text-slate-400 italic">Sin coincidencias</p>
              ) : (
                <div className="space-y-1">
                  {results.professors.map((u) => renderUserRow(u, 'prof',
                    <Button
                      size="sm"
                      className="h-7 bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => handleAdd(u.user_id, u.name)}
                      disabled={pendingId === u.user_id}
                      data-testid={`add-assistant-${u.user_id}`}
                    >
                      {pendingId === u.user_id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <>
                          <UserPlus className="w-3.5 h-3.5 mr-1" />
                          Agregar
                        </>
                      )}
                    </Button>
                  ))}
                </div>
              )}
            </div>

            {/* Students */}
            <div>
              <h4 className="text-[11px] font-semibold text-emerald-700 mb-1 flex items-center gap-1">
                <GraduationCap className="w-3.5 h-3.5" />
                Estudiantes inscritos en esta clase
              </h4>
              {searching ? null : results.students.length === 0 ? (
                <p className="text-xs text-slate-400 italic">Sin coincidencias</p>
              ) : (
                <div className="space-y-1">
                  {results.students.map((u) => renderUserRow(u, 'stu',
                    <Button
                      size="sm"
                      className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => handleAdd(u.user_id, u.name)}
                      disabled={pendingId === u.user_id}
                      data-testid={`add-assistant-${u.user_id}`}
                    >
                      {pendingId === u.user_id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <>
                          <UserPlus className="w-3.5 h-3.5 mr-1" />
                          Agregar
                        </>
                      )}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
